import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UsersApi } from '@api/users/users.api';
import { MaintenancesApi } from '@core/api/maintenances/maintenances.api';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { CustomMessagesService } from '@core/errors/custom-messages.service';
import { SocketService } from '@core/services/socket.service';
import { delay } from '@core/utils/delay';
import { Account, Maintenance, NotificationTypeEnum, User } from '@interfaces';
import { kebabCase, map } from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { MaintenanceDialogData } from '../../dialog-maintenances.component';

const notBlank: ValidatorFn = (control) => {
  if (control.value.trim() === '') {
    return {
      empty: true
    };
  }

  return null;
};

@Component({
  selector: 'tab-comments',
  templateUrl: './tab-comments.component.html',
  styleUrls: ['./tab-comments.component.scss'],
})
export class TabCommentsComponent implements OnInit, OnDestroy {
  public currentUser: User;

  public comments = [];
  public commentForm: FormGroup;
  public isLoading = false;
  private offset = 0;
  private limit = 10;
  public selected = 'internal';
  public maintenance: Maintenance;
  public isLoadingMore = false;
  private messageFormControl = new FormControl('', [Validators.required, Validators.maxLength(1000), notBlank]);
  private readonly subscriptions = new Subject<void>();
  private account: Account;
  public accountName: string;
  public fieldAccountName: string;

  @ViewChild('commentBody', { static: true })
  private commentBody: ElementRef<HTMLDivElement>;

  @ViewChild('commentTextArea', { static: true })
  private commentTextArea: ElementRef<HTMLTextAreaElement>;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: MaintenanceDialogData,
    private authenticationService: AuthenticationService,
    private customMessageService: CustomMessagesService,
    private maintenancesApi: MaintenancesApi,
    private socketService: SocketService,
    private formBuilder: FormBuilder,
    private usersApi: UsersApi
  ) {
    this.maintenance = this.data.maintenance;
  }
  ngOnDestroy(): void {
    this.subscriptions.next();
    this.subscriptions.complete();
  }

  async ngOnInit(): Promise<void> {
    this.commentForm = this.formBuilder.group({
      message: this.messageFormControl,
      type: ['internal', Validators.required],
    });

    this.currentUser = this.authenticationService.user;

    const { account } = await this.usersApi.me();
    this.account = account;
    this.accountName = account.name;
    this.fieldAccountName = (this.maintenance.company && this.maintenance.company.name) || '';
    this.getComments();

    this.scrollToBottom();

    this.socketService.lastNotificationObs
      .pipe(
        takeUntil(this.subscriptions),
        filter((message) => message.type === kebabCase(NotificationTypeEnum.maintenanceCommented)),
        filter((message) => message.maintenance.id === this.maintenance.id),
        filter(({ comment }) => !map(this.comments, 'id').includes(comment.id))
      )
      .subscribe(({ comment }) => {
        this.comments.push(comment);
        this.scrollToBottom();
      });
  }

  async getComments(): Promise<void> {
    this.isLoading = true;
    await this.maintenancesApi
      .searchMaintenanceComment({ maintenanceId: this.maintenance.id, limit: this.limit, offset: this.offset })
      .pipe(debounceTime(500))
      .toPromise()
      .then((comments) => {

        if (!comments.length && this.isLoadingMore) {
          this.customMessageService.custom('MAINTENANCES.TABS.COMMENTS.NO_MORE_COMMENTS', 'chat');
          this.isLoading = false;
          this.isLoadingMore = false;
          return;
        }

        this.comments.unshift(...comments);
        this.isLoading = false;
      });
  }

  async scrollToBottom(): Promise<void> {
    await delay(10);
    this.commentBody.nativeElement.scrollTo({
      top: this.commentBody.nativeElement.scrollHeight,
    });
  }

  async onScroll(): Promise<void> {
    this.isLoadingMore = true;
    this.offset += this.limit;
    this.getComments();
  }

  async onKeydownEvent(event: KeyboardEvent): Promise<void> {
    if (this.commentForm.invalid) {
      return;
    }

    if (this.isLoading) {
      return;
    }

    if (event.key !== 'Enter') {
      return;
    }

    if (event.ctrlKey) {
      this.messageFormControl.setValue(`${this.messageFormControl.value}\n`);
      return;
    }

    await this.addComment();
  }

  isMe(comment): boolean {
    return comment.postedBy.id === this.currentUser.id;
  }

  removeComment(comment): void {
    this.maintenancesApi.removeMaintenanceComment(comment.id).subscribe();
    comment.archived = true;
  }

  async addComment(): Promise<void> {

    if (this.commentForm.invalid) {
      return;
    }

    this.messageFormControl.disable();

    const message = {
      message: this.messageFormControl.value.trim(),
      type: this.commentForm.value.type.trim(),
      maintenanceId: this.maintenance.id,
    };

    this.commentForm.controls.message.setValue('');

    const comment = await this.maintenancesApi.createMaintenanceComment(message);

    if (comment) {
      this.comments.push(comment);
    }

    await this.scrollToBottom();
    this.messageFormControl.enable();
    this.messageFormControl.setValue(this.messageFormControl.value.trim());
    this.commentTextArea.nativeElement.focus();
  }

  getPostedByName(comment): string {
    if (comment.postedBy.type === 'company' && comment.company) {
      return  `${comment.postedBy.name} - ${comment.company.name}`;
    }
    return `${comment.postedBy.name} - ${this.account.name}`;
  }
}
